/**
 * 左栏那份会话列表怎么分组。
 *
 * # 它修的是什么
 *
 * arron 2026-08-29：「左边对话列表也没有项目分类，项目分类也应该像
 * Claude Code 那样」。当时那一栏是一条平铺的列表，而且**只可能有
 * 不挂项目的会话**——`GET /conversations` 按契约就是「列出不挂项目的
 * 总控会话」。于是项目会话在这一栏里从来没出现过：既分不了类，
 * 从项目页深链过来也高亮不到。
 *
 * # 为什么分组算在这儿，不在 JSX 里
 *
 * 「哪条归哪个项目、组怎么排、组内怎么排」是三条规则，埋在渲染里的话
 * 只能靠截图去验。抽出来之后它们各有一条用例。
 */

/** 一条会话在左栏里要的那点东西。 */
export interface RailConversation {
  readonly id: string
  readonly title: string
  readonly projectId: string | null
  /** ISO 时间串。排序用。 */
  readonly updatedAt: string
}

/** 一组。`projectId` 为 null 的那组是「不挂项目」。 */
export interface RailGroup {
  readonly projectId: string | null
  readonly name: string
  readonly conversations: readonly RailConversation[]
}

/**
 * 把会话按项目分组。
 *
 * # 三条规则
 *
 *   组内   最近更新的在上（与两条 list 接口自己的排序一致）
 *   组间   **不挂项目那组永远在最前**，其余按各自最新那条的时间倒序
 *   命名   项目名从 projects 取；**取不到的项目整组不显示**
 *
 * 第三条是刻意的：一个标题写着裸 uuid 的分组，比不显示更糟——
 * 他看不出那是哪个项目，而这一栏的全部用处就是让他一眼找到地方。
 * 项目名没取到通常只是那次请求还没回来，下一轮就有了。
 *
 * 「不挂项目」那组永远在最前，是因为它是 /lulu 的默认落点：
 * 把它排到中间，他每次进来都要先找自己刚才在哪儿。
 */
export function groupRailConversations(
  conversations: readonly RailConversation[],
  projects: readonly { readonly id: string; readonly name: string }[],
  generalGroupName: string,
): readonly RailGroup[] {
  const nameOf = new Map(projects.map((p) => [p.id, p.name]))
  const byProject = new Map<string | null, RailConversation[]>()
  for (const c of conversations) {
    // 认不出的项目整组丢掉——见上面第三条。
    if (c.projectId !== null && !nameOf.has(c.projectId)) continue
    const bucket = byProject.get(c.projectId)
    if (bucket) bucket.push(c)
    else byProject.set(c.projectId, [c])
  }
  const groups: RailGroup[] = []
  for (const [projectId, list] of byProject) {
    const sorted = [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    groups.push({
      projectId,
      name: projectId === null ? generalGroupName : (nameOf.get(projectId) ?? ''),
      conversations: sorted,
    })
  }
  return groups.sort((a, b) => {
    if (a.projectId === null) return -1
    if (b.projectId === null) return 1
    const at = a.conversations[0]?.updatedAt ?? ''
    const bt = b.conversations[0]?.updatedAt ?? ''
    return bt.localeCompare(at)
  })
}

/**
 * 等 Lulu 接话已经多少秒。`null` = 没在等。
 *
 * # 为什么抽出来
 *
 * 它原来是 index.tsx 里的一段内联计算，而 view 那侧的用例是把结果
 * 直接喂进去的——**于是「秒数真的在走」这件事没有任何东西守着**。
 * 变异验证当场发现：把它改成恒回 0，77 条用例全绿。
 *
 * # 三条规矩
 *
 *   没在等          回 null，不是 0——0 会让那一行一直挂着
 *   刚发出去那一刻   回 0，而那一行要出现（他按下发送就该看到反馈）
 *   时钟往回走       回 0，不回负数
 *
 * 第三条不是洁癖：系统时间被 NTP 往回校正是真会发生的，
 * 而屏幕上一个「-3 秒」会让他以为这一屏坏了。
 */
export function thinkingSecondsOf(awaitingSince: number | null, now: number): number | null {
  if (awaitingSince === null) return null
  return Math.max(0, Math.floor((now - awaitingSince) / 1000))
}

/**
 * 「我在做这件事」那三句进度语的文案 id。
 *
 * **从 @npc/copy 取文本，不在这儿抄一份。** 抄一份的话，模板改了措辞
 * 而这里没跟上，认不出来的表现是——进度语被当成答案，「思考中」当场停掉，
 * 然后三十几秒后凭空冒出第二条。而那时没有任何一侧会红。
 */
const WORKING_COPY_IDS = [
  'steward.smalltalk.working.list_projects',
  'steward.smalltalk.working.create_project',
  'steward.smalltalk.working.web_search',
] as const

/**
 * isWorkingLine 说这条消息是不是「我在做这件事」那句进度语。
 *
 * # 为什么要认得它
 *
 * 用工具那一轮 Lulu 说**两句**：先一句进度语（几秒就到），
 * 再一句真正的答案（实测 33～40 秒后）。而「还在等回话」原来的判据是
 * 「有没有出现别人说的消息」——进度语正好满足它。
 *
 * 于是屏幕会在进度语到达的那一刻说「回完了」，然后三十几秒后凭空
 * 多出一条。**那比没有进度语更糟**：它先给了一个「结束了」的错信号。
 *
 * 判据来自 @npc/copy 的生成物：模板改了文案，这里跟着变，
 * 不需要任何人记得同步。id 写错的话 lookup 回 undefined，
 * 那条就永远认不出来——所以下面那条用例逐个核 id 真的查得到。
 */
export function isWorkingLine(
  body: string,
  lookupCopy: (id: string) => { text: string } | undefined,
): boolean {
  const said = body.trim()
  if (said === '') return false
  if (WORKING_COPY_IDS.some((id) => lookupCopy(id)?.text.trim() === said)) return true
  // 带变量的进度语按变量前面那一截认：技能「先说一声」那句（steward.finding.restate）
  // 之后拆计划还要几十秒，它不是答案。2026-09-04 生产实测：它一到「正在想」就被收掉了。
  return WORKING_PREFIX_IDS.some((id) => {
    const text = lookupCopy(id)?.text ?? ''
    const head = text.split('{')[0]?.trim() ?? ''
    return head.length >= 4 && said.startsWith(head)
  })
}

/** 带变量、按前缀认的进度语。 */
const WORKING_PREFIX_IDS = ['steward.finding.restate'] as const

/** workingPrefixIds 导出给用例核对：每一个 id 都必须在文案库里查得到，而且带变量。 */
export function workingPrefixIds(): readonly string[] {
  return WORKING_PREFIX_IDS
}

/** workingCopyIds 导出给用例核对：每一个 id 都必须在文案库里查得到。 */
export function workingCopyIds(): readonly string[] {
  return WORKING_COPY_IDS
}

/**
 * 等回话时那一行该显示的「这一刻在做什么」。
 *
 * 取这条会话过程流里**最新的一条**，但只认这次等待开始之后到达的：
 * 上一轮的收口事件（run.finished）永远是流里最后一条，不筛掉的话，
 * 每次刚按下发送，屏幕上写的都是上一轮的「做完了」。
 *
 * 判据用时间而不是 run id：这一轮的 run id 要等 request_accepted 到了才知道，
 * 而那之前也该显示秒数。返回 null = 还没有任何这一轮的事件，退回「正在想」。
 */
export function thinkingPhaseOf(
  steps: readonly { readonly at: string; readonly summary: string }[] | undefined,
  thinkingSeconds: number | null,
  nowMs: number,
): string | null {
  if (thinkingSeconds === null || !steps || steps.length === 0) return null
  // 留 2 秒余量：两边的时钟不是同一只。
  const since = nowMs - thinkingSeconds * 1000 - 2_000
  for (let i = steps.length - 1; i >= 0; i--) {
    const at = Date.parse(steps[i]!.at)
    if (Number.isNaN(at) || at < since) return null
    const summary = steps[i]!.summary.trim()
    if (summary !== '') return summary
  }
  return null
}

/**
 * 「这次等的那句回话到了没」。
 *
 * 判据是 **sequence 在我刚发的那条之后**、说话的不是我、而且不是一句进度语。
 * 上一版看的是「列表里有没有别人说的话」——而任何一条聊过两句的会话里都有，
 * 于是等待状态在按下发送的同一刻就被清掉，「正在想」永远显示不出来
 * （2026-09-04 在生产上量到：li 一次都没出现过）。
 */
/**
 * 这一条发出去之后，要不要进「等 Lulu 回」那个状态。
 *
 * ------------------------------------------------------------------
 * # 你 @ 的是人，本来就不是在等 Lulu
 *
 * 2026-09-08 L-63 让 Lulu 在 @ 人时沉默——他在跟人说话，Lulu 插嘴是噪音。
 * 而前端清「思考中」的判据是 replyArrived：**收到一条别人的消息**。
 * Lulu 不说话，这个条件永远不成立，于是等到 effort 那档的超时预算才清，
 * 屏幕上就是「小队长一直在思考」——arron 当天就报了。
 *
 * 所以判据放在发送那一刻：**只 @ 了人、没 @ 任何 AI → 不进等待态**。
 * @ 了 AI 或者谁都没 @，照旧等——那两种情况下确实有人要回。
 *
 * 被 @ 的人不在场时服务端会回一句「他不在这条会话里」，那句照样能到：
 * 消息写入推进 durable revision，SSE 触发重取，不靠这个等待态的快轮询。
 * ------------------------------------------------------------------
 */
export function shouldAwaitReply(
  mentions: readonly { readonly executor_id: string }[],
): boolean {
  const people = mentions.some((m) => m.executor_id.startsWith('user:'))
  const ais = mentions.some((m) => m.executor_id.startsWith('agent:'))
  return !(people && !ais)
}

export function replyArrived(
  list: readonly {
    readonly sequence: number
    readonly participant_id?: string
    readonly body: string
  }[],
  sentSequence: number,
  mine: string | undefined,
  isProgress: (body: string) => boolean,
): boolean {
  return list.some(
    (m) =>
      m.sequence > sentSequence &&
      m.participant_id !== undefined &&
      m.participant_id !== mine &&
      !isProgress(m.body),
  )
}

/**
 * 等回话最多等多久，按发这条时选的档位。
 *
 * 这是「服务端那边真的出事了」的判据，不是期望值：服务端单轮预算
 * （stewardplan.turnBudgetFor：自动 6 分钟、快速 3、标准 8、最高 14）再加 30 秒余量。
 * **留在它上面**：低于服务端预算的话，Lulu 还在正常地想，屏幕上却先把
 * 「正在想」收掉了、轮询也退回 5 秒一次（上一版 100 秒 < 2 分钟，就是这个样子）。
 * 与 orchestrator router 的墙钟表、control 的预算表是同一张表的抄本，改一处要一起改。
 */
export function awaitReplyBudgetMs(effort: 'fast' | 'standard' | 'max' | null): number {
  const minute = 60_000
  const slack = 30_000
  switch (effort) {
    case 'fast':
      return 3 * minute + slack
    case 'standard':
      return 8 * minute + slack
    case 'max':
      return 14 * minute + slack
    default:
      return 6 * minute + slack
  }
}

/**
 * 这次等待里已经烧掉的 token：取这次等待开始之后最新一条带 tokens 的事件。
 * 与 thinkingPhaseOf 同一套时间判据；没有任何一跳回来之前是 null。
 */
export function thinkingTokensOf(
  steps: readonly { readonly at: string; readonly tokens?: number }[] | undefined,
  thinkingSeconds: number | null,
  nowMs: number,
): number | null {
  if (thinkingSeconds === null || !steps || steps.length === 0) return null
  const since = nowMs - thinkingSeconds * 1000 - 2_000
  for (let i = steps.length - 1; i >= 0; i--) {
    const at = Date.parse(steps[i]!.at)
    if (Number.isNaN(at) || at < since) return null
    const tokens = steps[i]!.tokens
    if (typeof tokens === 'number' && tokens > 0) return tokens
  }
  return null
}

/** 照 Claude Code 那一行的写法缩 token 数：999 → 999，1234 → 1.2k，12345 → 12.3k。 */
export function formatTokens(n: number): string {
  if (n < 1000) return String(n)
  const k = n / 1000
  return `${k < 10 ? k.toFixed(1) : k.toFixed(1)}k`
}
